import ZoomVideoSDK, { type ExecutedResult } from '@zoom/videosdk';

import { getBackgroundImageUrl } from '../../core/backgrounds';
import type { LocalAudioDelegator, LocalVideoDelegator } from '../../core/room/participant/media/MediaDelegator';
import {
  BackgroundSettings,
  BackgroundType,
  type LocalAudioContextSetter,
  LocalMediaStatus,
  type LocalVideoContextSetter,
  type StartVideoResult,
} from '../../core/types';
import { getStoredAudioVideoSettings } from '../../core/utils';
import { VirtualBackgroundArgs } from '../types';
import { toStartVideoError } from './error';
import { handleError } from './handleError';

type MediaOperationArgs<T> = {
  operation: () => ExecutedResult | Promise<void | Error>;
  getDelegator: () => T;
  successStatus: LocalMediaStatus;
  errorContext: string;
};

interface AudioOperationArgs extends MediaOperationArgs<LocalAudioDelegator> {
  setLocalAudioContext: LocalAudioContextSetter;
}

/**
 * Helper for performing Zoom audio operations.
 * Handles the pattern: set loading → perform operation → handle error or success.
 */
export async function performZoomAudioOperation(args: AudioOperationArgs): Promise<boolean> {
  const { setLocalAudioContext, operation, getDelegator, successStatus, errorContext } = args;

  setLocalAudioContext((previous) => ({
    ...previous,
    status: LocalMediaStatus.LOADING,
  }));

  try {
    await operation();
  } catch (error) {
    handleError(errorContext, error);
    setLocalAudioContext((previous) => ({
      ...previous,
      status: LocalMediaStatus.ERROR,
    }));
    return false;
  }

  setLocalAudioContext({
    delegator: getDelegator(),
    status: successStatus,
  });

  return true;
}

interface VideoOperationArgs extends MediaOperationArgs<LocalVideoDelegator> {
  setLocalVideoContext: LocalVideoContextSetter;
}

/**
 * @deprecated - Use {@link tryZoomVideoOperation} instead, which surfaces the
 * failure reason so callers can branch on it.
 */
export async function performZoomVideoOperation(args: VideoOperationArgs): Promise<boolean> {
  const [success] = await tryZoomVideoOperation(args);
  return success;
}

/**
 * Same as {@link performZoomVideoOperation}, but surfaces the failure so callers
 * can branch on why the operation failed, e.g. to tell a camera held by another
 * application apart from a missing browser permission.
 */
export async function tryZoomVideoOperation(args: VideoOperationArgs): Promise<StartVideoResult> {
  const { setLocalVideoContext, operation, getDelegator, successStatus, errorContext } = args;

  setLocalVideoContext((previous) => ({
    ...previous,
    status: LocalMediaStatus.LOADING,
  }));

  try {
    await operation();
  } catch (error) {
    handleError(errorContext, error);
    setLocalVideoContext((previous) => ({
      ...previous,
      status: LocalMediaStatus.ERROR,
    }));
    return [false, toStartVideoError(error)];
  }

  setLocalVideoContext({
    delegator: getDelegator(),
    status: successStatus,
  });

  return [true, null];
}

type PermissionResult<T extends boolean> = T extends true
  ? { permissionGranted: true; deviceId: string }
  : { permissionGranted: false; deviceId: undefined };

type PermissionPromptResults = {
  audio: PermissionResult<boolean>;
  video: PermissionResult<boolean>;
};

export async function promptAudioVideoPermission(): Promise<PermissionPromptResults> {
  // prompts for both audio and video device permissions in a single request.
  const devices = await ZoomVideoSDK.getDevices();

  const audioInputDeviceId = devices.find(({ deviceId, kind }) => {
    return deviceId !== '' && kind === 'audioinput';
  })?.deviceId;
  const audioPermissionGranted = Boolean(audioInputDeviceId);

  const videoInputDeviceId = devices.find(({ deviceId, kind }) => {
    return deviceId !== '' && kind === 'videoinput';
  })?.deviceId;
  const videoPermissionGranted = Boolean(videoInputDeviceId);

  const { inputAudioDeviceId: storedAudioInputDeviceId, inputVideoDeviceId: storedVideoInputDeviceId } =
    getStoredAudioVideoSettings();

  const audioDeviceId = storedAudioInputDeviceId || audioInputDeviceId;
  const videoDeviceId = storedVideoInputDeviceId || videoInputDeviceId;

  return {
    audio:
      audioPermissionGranted && audioDeviceId
        ? { permissionGranted: true, deviceId: audioDeviceId }
        : { permissionGranted: false, deviceId: undefined },
    video:
      videoPermissionGranted && videoDeviceId
        ? { permissionGranted: true, deviceId: videoDeviceId }
        : { permissionGranted: false, deviceId: undefined },
  };
}

/**
 * Determines the virtual background and returns a tuple of the background type
 * and the value that Zoom expects for `imageUrl` when starting the video track
 * with a virtual background.
 */
export async function getVirtualBackgroundArg(
  backgroundType: BackgroundSettings['backgroundType'],
  backgroundIndex: BackgroundSettings['backgroundIndex'],
  isVirtualBackgroundSupported?: boolean,
): Promise<VirtualBackgroundArgs> {
  if (!isVirtualBackgroundSupported) {
    return {};
  }

  switch (backgroundType) {
    case BackgroundType.NONE:
      return {
        virtualBackgroundType: BackgroundType.NONE,
        virtualBackgroundValue: undefined,
      };
    case BackgroundType.BLUR:
      return {
        virtualBackgroundType: BackgroundType.BLUR,
        virtualBackgroundValue: { imageUrl: BackgroundType.BLUR },
      };
    case BackgroundType.IMAGE: {
      if (backgroundIndex === undefined) {
        throw new Error('Background index is required for image type');
      }
      const { src } = await getBackgroundImageUrl(backgroundIndex);
      return {
        virtualBackgroundType: BackgroundType.IMAGE,
        virtualBackgroundValue: {
          imageUrl: src,
        },
      };
    }
    default: {
      const _exhaustiveCheck: never = backgroundType;
      return _exhaustiveCheck;
    }
  }
}
